import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { GlassCard } from '@/components/ui/glass-card'
import { ProcessSteps } from '@/components/ProcessStep'
import { TrustBadgeGrid } from '@/components/TrustBadge'
import { useLuxeHero } from '@gjl/motion/css'
import { useLuxeReveal, useLuxeGrid } from '@gjl/motion/css'
import { Shield, Eye, Clock, TrendingUp, ArrowRight } from 'lucide-react'

export default function HomePage() {
  const heroRef = useLuxeHero<HTMLDivElement>(0.2)
  const credibilityRef = useLuxeGrid<HTMLDivElement>('.trust-badge')
  const beliefsRef = useLuxeGrid<HTMLDivElement>('.belief-card')
  const processRef = useLuxeReveal<HTMLDivElement>('.reveal')
  const founderRef = useLuxeReveal<HTMLDivElement>('.reveal')
  const ctaRef = useLuxeReveal<HTMLDivElement>('.reveal')

  return (
    <div className="bg-background text-foreground">
      {/* Hero Section */}
      <section className="relative -mt-16 flex min-h-[90vh] items-center justify-center overflow-hidden pt-16">
        {/* Video Background */}
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster="/hero-poster.jpg"
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
        >
          <source src="/hero-video.mp4" type="video/mp4" />
        </video>

        {/* Dark overlay */}
        <div className="hero-overlay absolute inset-0" />

        {/* Content */}
        <div ref={heroRef} className="relative z-10 mx-auto max-w-4xl px-4 text-center">
          <p className="text-primary mb-6 text-lg font-medium tracking-wide">Multifamily real estate</p>
          <h1 className="text-fluid-h1 text-foreground mb-6">Multifamily ownership without the operations.</h1>
          <p className="text-fluid-body-lg text-muted-foreground mx-auto mb-10 max-w-2xl">
            We source, underwrite, and manage. You receive quarterly reports and distributions.
          </p>
          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <Link to="/invest">
              <Button size="xl">
                See How It Works
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/strategy">
              <Button variant="outline" size="xl">
                Our Strategy
              </Button>
            </Link>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="border-muted-foreground/30 flex h-10 w-6 items-start justify-center rounded-full border-2 p-2">
            <div className="bg-primary h-2 w-1 rounded-full" />
          </div>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section ref={credibilityRef} className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-5xl">
          <TrustBadgeGrid
            badges={[
              {
                icon: <Shield className="h-5 w-5" />,
                title: 'Stress-Tested Underwriting',
                description:
                  'Every deal is modeled against rising vacancies, rate hikes, and deferred maintenance before we close.',
              },
              {
                icon: <Eye className="h-5 w-5" />,
                title: 'Detailed Reporting',
                description:
                  'Quarterly financials, occupancy updates, and market analysis for every property in the portfolio.',
              },
              {
                icon: <Clock className="h-5 w-5" />,
                title: 'Long-Term Focus',
                description: 'We think in decades, not quarters. Real wealth compounds slowly.',
              },
            ]}
          />
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-4xl" />

      {/* What We Believe Section */}
      <section ref={beliefsRef} className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-5xl">
          <div className="mb-12 text-center">
            <h2 className="text-fluid-h2 text-foreground mb-4">What We Believe</h2>
            <p className="text-muted-foreground mx-auto max-w-2xl text-lg">
              These principles guide every decision we make
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <GlassCard className="belief-card" hover>
              <div className="flex items-start gap-4">
                <div className="bg-primary mt-2 h-3 w-3 shrink-0 rounded-full" />
                <div>
                  <h3 className="text-foreground mb-2 text-xl font-semibold">Protect capital first, grow it second</h3>
                  <p className="text-muted-foreground">
                    We stress-test every deal against rising vacancies, rate hikes, and deferred maintenance. If the
                    numbers don't work under pressure, we pass.
                  </p>
                </div>
              </div>
            </GlassCard>

            <GlassCard className="belief-card" hover>
              <div className="flex items-start gap-4">
                <div className="bg-primary mt-2 h-3 w-3 shrink-0 rounded-full" />
                <div>
                  <h3 className="text-foreground mb-2 text-xl font-semibold">Investors deserve full visibility</h3>
                  <p className="text-muted-foreground">
                    Quarterly reports cover property-level financials, occupancy, maintenance, and market conditions.
                    You'll always know how your capital is performing.
                  </p>
                </div>
              </div>
            </GlassCard>

            <GlassCard className="belief-card" hover>
              <div className="flex items-start gap-4">
                <div className="bg-primary mt-2 h-3 w-3 shrink-0 rounded-full" />
                <div>
                  <h3 className="text-foreground mb-2 text-xl font-semibold">Think in decades, not quarters</h3>
                  <p className="text-muted-foreground">
                    Real wealth compounds slowly. We're building a portfolio that works across market cycles, not
                    chasing quick wins.
                  </p>
                </div>
              </div>
            </GlassCard>

            <GlassCard className="belief-card" hover>
              <div className="flex items-start gap-4">
                <div className="bg-primary mt-2 h-3 w-3 shrink-0 rounded-full" />
                <div>
                  <h3 className="text-foreground mb-2 text-xl font-semibold">People over transactions</h3>
                  <p className="text-muted-foreground">
                    We structure partnerships around the investor's goals, not just the deal. Every decision starts with
                    who's at the table.
                  </p>
                </div>
              </div>
            </GlassCard>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-4xl" />

      {/* How It Works Section */}
      <section ref={processRef} className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-4xl">
          <div className="reveal mb-12 text-center">
            <h2 className="text-fluid-h2 text-foreground mb-4">How It Works</h2>
            <p className="text-muted-foreground mx-auto max-w-2xl text-lg">
              From investor list to quarterly distributions in three steps
            </p>
          </div>

          <div className="reveal">
            <ProcessSteps
              steps={[
                {
                  title: 'Join the Investor List',
                  description:
                    'Sign up to receive vetted investment opportunities directly. We only reach out when we have a deal worth your attention.',
                },
                {
                  title: 'Review the Deal',
                  description:
                    "You'll get detailed projections, comparable sales, risk analysis, and our assessment of what could go wrong. Take the time you need.",
                },
                {
                  title: 'Invest Confidently',
                  description:
                    'Contribute capital on your terms. We handle acquisition, renovation, leasing, and management. You receive quarterly reports and distributions.',
                },
              ]}
            />
          </div>

          <div className="reveal mt-8 text-center">
            <Link to="/invest">
              <Button size="lg">
                Learn More About Partnering
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-4xl" />

      {/* Track Record Teaser */}
      <section className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-5xl">
          <GlassCard accent className="overflow-hidden">
            <div className="grid items-center gap-8 md:grid-cols-2">
              <div>
                <div className="mb-4 flex items-center gap-2">
                  <TrendingUp className="text-primary h-5 w-5" />
                  <span className="text-primary font-medium">First Deal Closed</span>
                </div>
                <h3 className="text-foreground mb-4 text-2xl font-bold md:text-3xl">West Boulevard Duplex</h3>
                <p className="text-muted-foreground mb-6">
                  A fully occupied duplex in Cleveland's West Boulevard neighborhood. Closed April 2025 - generating
                  cash flow from day one.
                </p>
                <div className="mb-6 grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-foreground text-2xl font-bold">$170K</div>
                    <div className="text-muted-foreground text-sm">Purchase Price</div>
                  </div>
                  <div>
                    <div className="text-primary text-2xl font-bold">100%</div>
                    <div className="text-muted-foreground text-sm">Occupied</div>
                  </div>
                </div>
                <Link to="/track-record">
                  <Button variant="outline-gold">
                    View Full Details
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
              <div className="relative aspect-[4/3] overflow-hidden rounded-xl">
                <img
                  src="/duplex-westblvd.webp"
                  alt="West Boulevard duplex in Cleveland"
                  className="h-full w-full object-cover"
                />
                <div className="from-background/60 absolute inset-0 bg-gradient-to-t to-transparent" />
              </div>
            </div>
          </GlassCard>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-4xl" />

      {/* About Founder Section */}
      <section ref={founderRef} className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-4xl">
          <div className="reveal flex flex-col items-center gap-8 md:flex-row md:items-start md:gap-12">
            <div className="relative shrink-0">
              <div className="ring-primary/20 h-48 w-48 overflow-hidden rounded-2xl shadow-2xl ring-4 md:h-56 md:w-56">
                <img
                  src="/headshot.webp"
                  alt="Garrett John Law, Founder of Fairmeadow Capital"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
            <div className="text-center md:text-left">
              <h2 className="text-fluid-h3 text-foreground mb-4">Meet the Founder</h2>
              <p className="text-fluid-body-lg text-muted-foreground mb-6">
                Garrett John Law is a software engineer, real estate investor, and founder of Fairmeadow Capital. He
                combines data-driven analysis with hands-on experience to help investors achieve passive income through
                residential real estate.
              </p>
              <Link
                to="/about"
                className="text-primary hover:text-primary/80 group inline-flex items-center font-medium transition-colors"
              >
                Read More About Garrett
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section ref={ctaRef} className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-4xl">
          <GlassCard accent className="reveal text-center">
            <h2 className="text-fluid-h2 text-foreground mb-4">Interested in Our Next Deal?</h2>
            <p className="text-muted-foreground mx-auto mb-8 max-w-2xl text-lg">
              Join the investor list and get early access to our upcoming deals.
            </p>
            <Link to="/contact">
              <Button size="xl">
                Join the Investor List
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <p className="text-muted-foreground mt-6 text-sm">
              No spam, just deal alerts, market insights, and educational content.
            </p>
          </GlassCard>
        </div>
      </section>
    </div>
  )
}
